import { PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useMember } from '../../contexts/MemberContext';
import Sidebar from '../common/Sidebar';

export default function ManageOthers() {
  const { memberships, loading, error, fetchMemberships } = useMember();

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  // Get primary membership
  const primaryMembership =
    memberships.find(m => m.status === 'active' && m.isPrimary) ||
    memberships.find(m => m.status === 'active') ||
    memberships[0] ||
    null;

  // Get other memberships (exclude primary)
  const otherMemberships = memberships.filter(m => m.id !== primaryMembership?.id);

  // Filter by status
  const activeOthers = otherMemberships.filter(m => m.status === 'active');
  const expiredOthers = otherMemberships.filter(m => m.status === 'expired');

  const handleMembershipClick = membershipId => {
    // In a real app, this would navigate to membership detail page
    console.log(`Clicked membership: ${membershipId}`);
  };

  // Calculate renewal status
  const getRenewalStatus = validUntil => {
    const today = new Date();
    const renewalDate = new Date(validUntil);
    const daysUntil = Math.ceil((renewalDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) {
      return {
        status: 'expired',
        color: 'text-red-400',
        message: 'Expired',
      };
    } else if (daysUntil <= 30) {
      return {
        status: 'urgent',
        color: 'text-red-400',
        message: `${daysUntil} days remaining`,
      };
    } else if (daysUntil <= 60) {
      return {
        status: 'soon',
        color: 'text-yellow-400',
        message: `${daysUntil} days remaining`,
      };
    } else {
      return {
        status: 'active',
        color: 'text-green-400',
        message: `${daysUntil} days remaining`,
      };
    }
  };

  return (
    <div className="min-h-screen bg-hdlcc-black">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-64">
          <div className="p-6">
            <div className="bg-hdlcc-darkGray text-white p-6 rounded-lg shadow-md">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold">Manage Other Memberships</h2>
                  <p className="text-gray-400">
                    View and manage memberships for family members or others
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Link
                    to="/membership/register"
                    className="flex items-center gap-2 bg-hdlcc-lightBlue text-white px-4 py-2 rounded hover:bg-hdlcc-blue"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add Membership
                  </Link>
                </div>
              </div>

              {loading && (
                <div className="text-center py-8">
                  <p>Loading memberships...</p>
                </div>
              )}

              {error && (
                <div className="bg-red-900 text-red-200 p-4 rounded mb-6">
                  <p>{error}</p>
                  <button
                    onClick={fetchMemberships}
                    className="mt-2 text-white bg-red-800 px-4 py-2 rounded hover:bg-red-700"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!loading && !error && otherMemberships.length === 0 && (
                <div className="text-center py-12">
                  <UserGroupIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">
                    You are not currently managing any other memberships
                  </p>
                  <Link
                    to="/membership/register"
                    className="inline-flex items-center gap-2 bg-hdlcc-lightBlue text-white px-4 py-2 rounded hover:bg-hdlcc-blue"
                  >
                    <PlusIcon className="w-5 h-5" />
                    Add a Membership
                  </Link>
                </div>
              )}

              {!loading && !error && otherMemberships.length > 0 && (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-hdlcc-gray p-4 rounded shadow">
                      <h4 className="text-gray-400 text-sm">Total Managed</h4>
                      <p className="text-3xl font-bold mt-2">{otherMemberships.length}</p>
                    </div>
                    <div className="bg-hdlcc-gray p-4 rounded shadow">
                      <h4 className="text-gray-400 text-sm">Active</h4>
                      <p className="text-3xl font-bold mt-2 text-green-400">
                        {activeOthers.length}
                      </p>
                    </div>
                    <div className="bg-hdlcc-gray p-4 rounded shadow">
                      <h4 className="text-gray-400 text-sm">Expired</h4>
                      <p className="text-3xl font-bold mt-2 text-red-400">{expiredOthers.length}</p>
                    </div>
                  </div>

                  {/* Active Memberships */}
                  {activeOthers.length > 0 && (
                    <div className="bg-hdlcc-gray p-4 rounded shadow mb-6">
                      <h3 className="text-lg font-bold mb-4">Active Memberships</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b border-hdlcc-darkGray">
                              <th className="px-4 py-2 text-left">Membership No.</th>
                              <th className="px-4 py-2 text-left">Type</th>
                              <th className="px-4 py-2 text-left">Member</th>
                              <th className="px-4 py-2 text-left">Valid Until</th>
                              <th className="px-4 py-2 text-left">Status</th>
                              <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {activeOthers.map(membership => {
                              const renewalStatus = getRenewalStatus(membership.validUntil);
                              return (
                                <tr
                                  key={membership.id}
                                  className="border-b border-hdlcc-darkGray/50 hover:bg-hdlcc-darkGray cursor-pointer"
                                  onClick={() => handleMembershipClick(membership.id)}
                                >
                                  <td className="px-4 py-2 font-mono">{membership.number}</td>
                                  <td className="px-4 py-2">{membership.type}</td>
                                  <td className="px-4 py-2">{membership.contact.email}</td>
                                  <td className="px-4 py-2">
                                    {new Date(membership.validUntil).toLocaleDateString()}
                                  </td>
                                  <td className={`px-4 py-2 ${renewalStatus.color}`}>
                                    {renewalStatus.message}
                                  </td>
                                  <td className="px-4 py-2">
                                    {renewalStatus.status === 'urgent' ||
                                    renewalStatus.status === 'soon' ? (
                                      <Link
                                        to="/renewal"
                                        onClick={e => e.stopPropagation()}
                                        className="bg-hdlcc-lightBlue text-white px-3 py-1 rounded hover:bg-hdlcc-blue text-sm"
                                      >
                                        Renew
                                      </Link>
                                    ) : (
                                      <span className="text-gray-400 text-sm">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Expired Memberships */}
                  {expiredOthers.length > 0 && (
                    <div className="bg-hdlcc-gray p-4 rounded shadow">
                      <h3 className="text-lg font-bold mb-4">Expired Memberships</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b border-hdlcc-darkGray">
                              <th className="px-4 py-2 text-left">Membership No.</th>
                              <th className="px-4 py-2 text-left">Type</th>
                              <th className="px-4 py-2 text-left">Member</th>
                              <th className="px-4 py-2 text-left">Expired On</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expiredOthers.map(membership => (
                              <tr
                                key={membership.id}
                                className="border-b border-hdlcc-darkGray/50 hover:bg-hdlcc-darkGray cursor-pointer"
                                onClick={() => handleMembershipClick(membership.id)}
                              >
                                <td className="px-4 py-2 font-mono">{membership.number}</td>
                                <td className="px-4 py-2">{membership.type}</td>
                                <td className="px-4 py-2">{membership.contact.email}</td>
                                <td className="px-4 py-2">
                                  {new Date(membership.validUntil).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
